import {
  IonButtons,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonToolbar,
  useIonAlert,
} from '@ionic/react';
import { chevronBack, pencil } from 'ionicons/icons';
import { RunModel } from 'models/RunModel/RunModel';
import {
  calcHighestSpeed,
  scaleData,
} from 'components/listElements/RunListItem/RunListItem';
import { useMemo, useState } from 'react';
import Trans from 'util/Trans';
import RunData from './components/RunData';
import { PlayFigure } from 'types/storage';
import { logger } from 'services/Logger';
import './css/ViewFigureInfo.css';
import DynamicButton from 'components/buttons/DynamicButton';
import { useUnitConversion } from 'hooks/useUnitConversion';

interface StatsSiderProps {
  record: RunModel;
  dismiss: () => void;
  isPublic?: boolean;
  isFriend?: boolean;
}

interface ExtendedPlayFigure extends PlayFigure {
  id: string;
  airtime: number;
  jumpheight: number;
  jumpwidth: number;
  deviceId: string;
  recordId: string;
  date: Date;
  shareUrl: string;
  localVideoFileName: string | null;
  trickName: string;
  score: number;
}

const ViewFigureInfo = (props: StatsSiderProps) => {
  const { record: item, dismiss, isPublic, isFriend } = props;
  const unitConversion = useUnitConversion();

  // Animation state management
  const [animationClass, setAnimationClass] = useState('animate__slideInRight');
  const [runName, setRunName] = useState(
    item.playData?.title || `Run #${item.playData?.id_record || ''}`
  );
  const [isEditingName, setIsEditingName] = useState(false);
  const [presentAlert] = useIonAlert();

  const handleDismiss = () => {
    // Start slide-out animation
    setAnimationClass('animate__slideOutRight');

    // Wait for animation to complete (300ms for animate__faster) then call dismiss
    setTimeout(() => {
      dismiss();
    }, 300);
  };

  const handleEditName = () => {
    presentAlert({
      header: 'Run umbenennen',
      inputs: [
        {
          name: 'newName',
          type: 'text',
          value: runName,
          placeholder: 'Neuer Name',
        },
      ],
      buttons: [
        {
          text: 'Abbrechen',
          role: 'cancel',
        },
        {
          text: 'OK',
          handler: (data) => {
            if (data.newName && data.newName.trim().length > 0) {
              const newName = data.newName.trim();
              setRunName(newName);
              if (item.playData) {
                item.setPlayData({
                  ...item.playData,
                  title: newName,
                });
              }
            }
          },
        },
      ],
    });
  };

  // Extract ranking data from playData
  const rankingData = item.playData?.ranking;

  const figures = useMemo(() => {
    if (!item.playData?.figures || !Array.isArray(item.playData.figures)) {
      logger.warn('Keine Figuren-Daten gefunden');
      return [];
    }

    return item.playData.figures.map((figure, index) => {
      const figureDetails = figure.details || {};
      const id = figure.i_beg
        ? `figure_${figure.i_beg}_${figure.i_end}`
        : `figure_${index}`;

      // Get score from ranking data if available
      let figureScore = 0;
      if (rankingData?.figure_scores) {
        const scoreData = rankingData.figure_scores.find(
          (fs: any) => fs.figure_index === index
        );
        figureScore = scoreData?.computed_score || 0;
      }

      return {
        id: id,
        airtime: figureDetails.air_time_s || 0,
        jumpheight: figureDetails.height_difference_m || 0,
        jumpwidth: figureDetails.fly_width_m || 0,
        deviceId: item.playData?.id_device || 'unknown',
        recordId: item.playData?.id_record || 'unknown',
        date: new Date(),
        shareUrl: '',
        localVideoFileName: null,
        trickName: figure.name || `Run #${index + 1}`,
        score: Math.round(figureScore * 10) / 10, // Round to 1 decimal
        achived: figure.achived || false,
        difficulty: figure.difficulty_percent || '',
        timeRange: {
          start: figure.i_beg,
          end: figure.i_end,
        },
      } as ExtendedPlayFigure;
    });
  }, [item.playData, rankingData]);

  // const tricks = Array.from(
  //   { length: 8 },
  //   (_, _index) =>
  //     ({
  //       id: `${Math.random()}`,
  //       dummy: Number.MAX_VALUE,
  //       height: parseFloat((Math.random() * 10).toFixed(2)),
  //       airtime: parseFloat((Math.random() * 5).toFixed(2)),
  //       jumpheight: parseFloat((Math.random() * 3).toFixed(2)),
  //       jumpwidth: parseFloat((Math.random() * 4).toFixed(2)),
  //       deviceId: 'dummyDeviceId',
  //       recordId: 'dummyRecordId',
  //       date: new Date(),
  //       shareUrl: 'dummyShareUrl',
  //       localVideoFileName: null,
  //       trickName: `Ollie`,
  //       score: Math.floor(Math.random() * 100),
  //     } as ExtendedPlayFigure)
  // );

  const tricksCount = figures.length;

  const details = item.playData?.details;
  const itemInfo = useMemo(() => {
    // Use unit conversion for distance measurement
    let distanceObj = unitConversion.scaleDistance(details?.travel_distance_m);
    let distance = `${distanceObj.value}${distanceObj.suffix}`;
    let airTime = scaleData(details?.air_time_s, 's');

    // Use unit conversion for speed display
    const speedValue = calcHighestSpeed(item.playData?.steps);
    let speed = '?? km/h';
    if (speedValue !== undefined) {
      const displayData = unitConversion.getDisplay(
        speedValue,
        'speed',
        'metric'
      ); // km/h is stored in metric
      speed = displayData.formatted;
    }

    // Use calculated score from ranking data, fallback to 0
    let score = rankingData?.play_score?.total_score || 0;
    return { distance, airTime, speed, score };
  }, [details, item.playData?.steps, rankingData, unitConversion]);

  logger.info('Data: ', item.playData);

  return (
    <IonContent scrollY={true}>
      <div
        id="trick-details-menu"
        className={`animate__animated ${animationClass} animate__faster view-run-info-meta notchSafe`}
      >
        <IonToolbar
          style={{
            background: 'transparent',
            boxShadow: 'none',
            border: 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              borderRadius: 16,
              margin: '0 auto 8px auto',
              maxWidth: 340,
            }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 0,
              }}
            >
              <span
                style={{
                  paddingTop: 4,
                  fontWeight: 700,
                  fontSize: 22,
                  color: '#fff',
                  textAlign: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: !isPublic && !isFriend ? 'pointer' : 'default',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
                onClick={!isPublic && !isFriend ? handleEditName : undefined}
                title={!isPublic && !isFriend ? 'Run umbenennen' : undefined}
              >
                {runName}
              </span>
              {!isPublic && !isFriend && (
                <button
                  aria-label="Run umbenennen"
                  onClick={handleEditName}
                  style={{
                    marginLeft: 8,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    color: '#fff',
                    fontSize: 22,
                    opacity: 0.8,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <IonIcon
                    icon={pencil}
                    style={{ fontSize: 20, marginRight: 2, color: 'inherit' }}
                  />
                </button>
              )}
            </div>
          </div>
          <IonButtons slot="end">
            <DynamicButton
              iconName="CancelUpIcon"
              buttonType="icon-full"
              size="large"
              fill="clear"
              className="spnx-button"
              onClick={handleDismiss}
            />
          </IonButtons>
        </IonToolbar>
        <IonList>
          <IonItem>
            <IonIcon
              src="/assets/icons/JudgeIcon.svg"
              slot="start"
              color="--ion-icon-color"
            />
            <IonLabel>
              <Trans i18nKey="SCORE">Score</Trans>
            </IonLabel>
            <IonLabel slot="end">
              <b>{Math.round(itemInfo.score * 10) / 10}</b>{' '}
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon
              src="/assets/icons/speed2.svg"
              slot="start"
              color="--ion-icon-color"
            />
            <IonLabel>
              <Trans i18nKey="MAXSPEED">Max Speed</Trans>
            </IonLabel>
            <IonLabel slot="end">{itemInfo.speed}</IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon
              src="/assets/icons/distance.svg"
              slot="start"
              color="--ion-icon-color"
            />
            <IonLabel>
              <Trans i18nKey="DISTANCE">Distanz</Trans>
            </IonLabel>
            <IonLabel slot="end">{itemInfo.distance}</IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon
              src="/assets/icons/location.svg"
              slot="start"
              color="--ion-icon-color"
            />
            <IonLabel>
              <Trans i18nKey="LOCATION">Ort</Trans>
            </IonLabel>
            <IonLabel slot="end">
              {item.playData?.location?.name && (
                <b>{item.playData.location.name}</b>
              )}
            </IonLabel>
          </IonItem>
          <IonItem>
            <IonIcon
              src="/assets/icons/TrickSpikeIcon.svg"
              slot="start"
              color="--ion-icon-color"
            />
            <IonLabel>
              <Trans i18nKey="TRICKS">Runs</Trans>
            </IonLabel>
            <IonLabel slot="end">{tricksCount}</IonLabel>
          </IonItem>

          {figures.map((figure) => (
            <RunData
              key={figure.id}
              item={figure as unknown as RunModel}
              distance={figure.jumpheight}
              height={figure.jumpheight}
              airtime={figure.airtime}
              width={figure.jumpwidth}
              figureName={figure.trickName}
              score={figure.score}
            />
          ))}
        </IonList>
      </div>
    </IonContent>
  );
};

export default ViewFigureInfo;

